import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CreditCard, FileText, Printer } from 'lucide-react';
import { paymentsAPI, tenantAPI } from '../../api/client';
import { formatRupees } from '../../lib/plans';
import { payInvoice } from '../../lib/razorpayCheckout';
import InvoiceDocument from './billing/InvoiceDocument';
import { ErrorBanner, Modal, useBasic } from './ui';

const date = (iso) => (iso ? new Date(`${iso}Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const STATUS = { open: 'Due', paid: 'Paid', failed: 'Payment failed' };

/** "Pay now": Razorpay Checkout for the invoice's balance. */
function usePay(onPaid) {
  const { notify } = useBasic();
  const [paying, setPaying] = useState(null);
  const [error, setError] = useState('');
  const pay = useCallback(async (inv) => {
    setPaying(inv.id);
    setError('');
    try {
      const r = await payInvoice(inv.id);
      if (r.status === 'paid') {
        notify(r.invoice_status === 'paid' ? `Payment received. Invoice ${inv.number} is paid. Thank you!` : `Payment of ${formatRupees(r.amount)} received for ${inv.number}.`);
        onPaid();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setPaying(null);
    }
  }, [notify, onPaid]);
  return { pay, paying, error };
}

/** The saved PDF's file name (browsers use the page title): TeleCalX_Invoice_TCX-2026-000014_Acme-Corp. */
export function invoiceFileName(page) {
  const safe = (s) => String(s || '').trim().replace(/[^A-Za-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  return ['TeleCalX_Invoice', safe(page.invoice.number), safe(page.customer?.name || page.invoice.company)].filter(Boolean).join('_');
}

/**
 * The printed copy: the same InvoiceDocument, as a direct child of <body> while the invoice is open, so print / Save as
 * PDF (the button or Ctrl+P) shows only it, on one A4 page (billing/invoice.css), saved under the invoice's name.
 */
function InvoicePrintSheet({ page }) {
  const name = invoiceFileName(page);
  useEffect(() => {
    const title = document.title;
    document.title = name;
    document.body.classList.add('printing-invoice');
    return () => {
      document.title = title;
      document.body.classList.remove('printing-invoice');
    };
  }, [name]);
  return createPortal(<div className="invoice-print-sheet"><InvoiceDocument page={page} /></div>, document.body);
}

/** One invoice (the document as it prints), with Print / Save as PDF and Pay now when online payment is on. */
function InvoiceView({ id, onClose, onPaid }) {
  const [page, setPage] = useState(null);
  const load = useCallback(() => tenantAPI.invoice(id).then((r) => setPage(r.data)).catch(() => setPage(false)), [id]);
  useEffect(() => { load(); }, [load]);
  const { pay, paying, error } = usePay(() => { load(); onPaid(); });
  const inv = page?.invoice;
  return (
    <Modal wide title={inv ? `Invoice ${inv.number}` : 'Invoice'} onClose={onClose}
      footer={inv && <>
        {page.pay_online && <button type="button" className="primary-action" disabled={!!paying} onClick={() => pay(inv)}>
          <CreditCard size={14} />{paying ? 'Opening Razorpay…' : `Pay ${formatRupees(Math.max(0, inv.total - (inv.paid || 0)))} now`}</button>}
        <button type="button" className={page.pay_online ? 'secondary-action' : 'primary-action'} onClick={() => window.print()}><Printer size={14} />Print / Save as PDF</button>
      </>}>
      <ErrorBanner message={error} />
      {page === null && <p>Loading…</p>}
      {page === false && <p>Could not load the invoice.</p>}
      {inv && <div className="invoice-desk"><InvoiceDocument page={page} /></div>}
      {inv && <InvoicePrintSheet page={page} />}
    </Modal>
  );
}

/** Billing & Plan: the company's invoices (owners). */
export default function InvoicesCard() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [online, setOnline] = useState(false);
  const load = useCallback(() => tenantAPI.invoices().then((r) => setRows(r.data.invoices)).catch(() => setRows([])), []);
  useEffect(() => {
    load();
    paymentsAPI.available().then((r) => setOnline(r.data.enabled)).catch(() => setOnline(false));
  }, [load]);
  const { pay, paying, error } = usePay(load);
  if (!rows?.length) return null;
  const unpaid = (inv) => inv.status === 'open' || inv.status === 'failed';
  return (
    <section className="panel page-info-card invoices-card">
      <div className="panel-heading"><h2>Invoices</h2>{online && <small className="table-subtitle">Pay online by card, UPI, net banking or wallet (Razorpay)</small>}</div>
      <ErrorBanner message={error} />
      <table className="data-table">
        <thead><tr><th>Invoice</th><th>Period</th><th>Due</th><th>Total</th><th>Status</th><th /></tr></thead>
        <tbody>
          {rows.map((inv) => (
            <tr key={inv.id}>
              <td>{inv.number}</td><td>{date(inv.period_start)} – {date(inv.period_end)}</td><td>{date(inv.due_at)}</td>
              <td>{formatRupees(inv.total)}</td><td><span className={`invoice-status ${inv.status}`}>{STATUS[inv.status] || inv.status}</span></td>
              <td><span className="row-actions">
                {online && unpaid(inv) && <button type="button" className="row-action call" disabled={!!paying} onClick={() => pay(inv)}>
                  <CreditCard size={13} />{paying === inv.id ? 'Opening…' : 'Pay now'}</button>}
                <button type="button" className="row-action" onClick={() => setOpen(inv.id)}><FileText size={13} />View</button>
              </span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && <InvoiceView id={open} onClose={() => setOpen(null)} onPaid={load} />}
    </section>
  );
}
